import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'

/**
 * @typedef {Object}DropZone
 */

/**
 * The upload file params
 * @component
 *
 * @param {Object} props
 * @param {function} [props.fileHandler]
 * @param {string} props.fileType
 * @param {boolean} props.open
 * @param {function} props.closePopup
 * @param {number} props.maxFiles
 * * @param {Object} props.fileData
 * @returns {JSX.Element}
 */

const DropZone = ({
  fileHandler = (file) => {},
  fileType = '',
  open = false,
  closePopup = () => {},
  maxFiles,
  fileData = {},
}) => {
  const onDrop = useCallback((files) => {
    if (files.length > maxFiles) {
      enqueueSnackbar('Solo se puede subir un fichero por operación', {
        variant: 'error',
      })
      return
    }
    const [file] = files
    const name = file.name
    const mimeType = file.type?.split('/')[1]
    if (fileType !== mimeType) {
      enqueueSnackbar(`El fichero debe de ser un ${fileType}`, {
        variant: 'warning',
      })
      return
    }
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      fileHandler({ data: reader.result.split('base64,')[1], fileName: name })
    }
    reader.onerror = () =>
      enqueueSnackbar('Se ha producido un error mientras se subía el fichero', {
        variant: 'error',
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  const removeFileHandler = (e) => {
    e.stopPropagation()
    fileHandler({ data: '', fileName: '' })
  }

  return (
    <Dialog
      key={fileType}
      open={open}
      onClose={closePopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle sx={{ p: '5px', display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ paddingLeft: '10px' }}>Fichero {fileType.toLocaleUpperCase()}</Typography>
        <CloseIcon
          sx={{
            cursor: 'pointer',
            border: '2px solid #426bba',
            borderRadius: '3px',
            m: '3px',
          }}
          onClick={closePopup}
        />
      </DialogTitle>
      <DialogContent
        sx={{
          p: '15px',
        }}
      >
        <Stack
          {...getRootProps({
            sx: {
              height: '90px',
              width: {
                xs: '460px',
                md: '500px',
              },
              padding: '0 10px',
              borderRadius: '5px',
              border: '1px dashed',
              cursor: 'pointer',
              borderColor: '#969799',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#e2e2e2',
            },
          })}
        >
          <input {...getInputProps()} />
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-evenly',
              '& > .MuiTypography-root': {
                fontSize: '13px',
                color: '#a6a3a3',
                wordBreak: 'break-all',
              },
            }}
          >
            {!isDragActive && <Typography>Zona para arrastrar y soltar ficheros</Typography>}
            {isDragActive && <Typography>Arrastra los ficheros aquí ...</Typography>}
            {!fileData.fileName && !isDragActive && (
              <Typography>Ningún fichero seleccionado</Typography>
            )}
            {!!fileData.fileName && !isDragActive && (
              <Typography>
                {fileData.fileName}
                <CancelOutlinedIcon
                  onClick={removeFileHandler}
                  sx={{
                    color: 'red',
                    width: '12px',
                    height: 'auto',
                    transition: 'transform .3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.2)',
                    },
                  }}
                />
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default DropZone
